import React, { useEffect, useRef, useState } from "react";
import { AgGridReact } from "ag-grid-react";
import { ColDef, ICellRendererParams } from "ag-grid-community";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import {
    Box,
    Typography,
    TextField,
    Button,
    Switch,
    Tabs,
    Tab,
    useTheme,
    Paper,
    Grid,
    Tooltip,
    IconButton,
    InputAdornment,
    Container
} from "@mui/material";
import { Add, Delete, Edit, FileDownload, FileUpload } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import API from "../api/axiosInstance";
import { ApprovalWorkflow } from "../types/interfaces";

const ApprovalAdmin: React.FC = () => {
    const theme = useTheme();
    const [tab, setTab] = useState(0);
    const [workflows, setWorkflows] = useState<any[]>([]);
    const [histories, setHistories] = useState<any[]>([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [filters, setFilters] = useState({ user: "", table: "", action: "", queue: "", from: "", to: "" });
    const gridRef = useRef<AgGridReact>(null);
    const navigate = useNavigate();

    useEffect(() => {
        loadWorkflows();
        loadHistories();
    }, []);

    const loadWorkflows = async () => {
        try {
            const res = await API.get("/approval/workflows");
            setWorkflows(res.data);
        } catch (err) {
            console.error("Error loading workflows", err);
        }
    };

    const loadHistories = async () => {
        try {
            const res = await API.get("/approval/history");
            setHistories(res.data);
        } catch (err) {
            console.error("Error loading history", err);
        }
    };

    const toggleWorkflow = async (workflow: any) => {
        try {
            await API.put(`/approval/workflows/${workflow.workflowID}`, { ...workflow, isEnabled: !workflow.isEnabled });
            loadWorkflows();
        } catch (err) {
            console.error("Toggle failed", err);
        }
    };

    const deleteWorkflow = async (workflowID: string) => {
        if (!window.confirm("Delete this workflow?")) return;
        try {
            await API.delete(`/approval/workflows/${workflowID}`);
            loadWorkflows();
        } catch (err) {
            console.error("Delete failed", err);
        }
    };

    const exportHistory = () => {
        const blob = new Blob([JSON.stringify(histories, null, 2)], { type: "application/json" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `approval-history.json`;
        link.click();
        URL.revokeObjectURL(url);
    };

    const handleExportWorkflows = () => {
        const blob = new Blob([JSON.stringify(workflows, null, 2)], { type: "application/json" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = "workflows.json";
        link.click();
        URL.revokeObjectURL(url);
    };

    const filteredWorkflows = workflows.filter((w) => w.name.toLowerCase().includes(searchTerm.toLowerCase()));
    const filteredHistories = histories.filter((h) =>
        (!filters.user || h.user?.toLowerCase().includes(filters.user.toLowerCase())) &&
        (!filters.table || h.tableName === filters.table) &&
        (!filters.action || h.action === filters.action) &&
        (!filters.queue || h.queueName === filters.queue) &&
        (!filters.from || new Date(h.actionAt) >= new Date(filters.from)) &&
        (!filters.to || new Date(h.actionAt) <= new Date(filters.to))
    );

    const workflowColumns: ColDef[] = [
        { field: "name", headerName: "Workflow", flex: 1 },
        { field: "tableName", headerName: "Table", flex: 1 },
        {
            headerName: "Enabled",
            field: "isEnabled",
            cellRenderer: (params: ICellRendererParams<ApprovalWorkflow>) => (
                <Switch checked={params.value} onChange={() => toggleWorkflow(params.data)} />
            ),
        },
        {
            headerName: "Actions",
            cellRenderer: (params: ICellRendererParams<ApprovalWorkflow>) => (
                <Box display="flex" gap={1}>
                    <Button variant="outlined" size="small" onClick={() => navigate(`/approvals/workflows/${params.data!.workflowID}/edit`)}>
                        <Edit fontSize="small" />
                    </Button>
                    <Button variant="outlined" color="error" size="small" onClick={() => deleteWorkflow(params.data!.workflowID)}>
                        <Delete fontSize="small" />
                    </Button>
                </Box>
            ),
        }
    ];

    const historyColumns: ColDef[] = [
        { field: "user", headerName: "User", flex: 1 },
        { field: "tableName", headerName: "Table", flex: 1 },
        { field: "queueName", headerName: "Queue", flex: 1 },
        { field: "action", headerName: "Action", flex: 1 },
        { field: "actionAt", headerName: "Date", flex: 1 },
    ];

    return (
        <Box sx={{ bgcolor: theme.palette.background.default, color: theme.palette.text.primary, minHeight: '100vh', py: 6 }}>
            <Container maxWidth="xl">
                <Typography variant="h5" gutterBottom>
                    Approval Administration
                </Typography>

                <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ mb: 3 }}>
                    <Tab label="Manage Workflows" />
                    <Tab label="Approval Histories" />
                </Tabs>

                {tab === 0 && (
                    <Paper sx={{ p: 3 }}>
                        <Grid container spacing={2} alignItems="center" justifyContent="space-between" mb={2}>
                            <Grid item xs={12} md={8}>
                                <TextField
                                    fullWidth
                                    label="Search Workflows"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                            </Grid>
                            <Grid item xs={12} md="auto">
                                <Button variant="contained" startIcon={<Add />} onClick={() => navigate("/approvals/workflows/new")}>Add Workflow</Button>
                                <IconButton component="label" sx={{ ml: 1 }}>
                                    <FileUpload />
                                    <input type="file" hidden accept=".json" onChange={() => { }} />
                                </IconButton>
                                <IconButton onClick={handleExportWorkflows}>
                                    <FileDownload />
                                </IconButton>
                            </Grid>
                        </Grid>

                        <Box className="ag-theme-alpine" sx={{ height: 500 }}>
                            <AgGridReact rowData={filteredWorkflows} columnDefs={workflowColumns} />
                        </Box>
                    </Paper>
                )}

                {tab === 1 && (
                    <Paper sx={{ p: 3 }}>
                        <Grid container spacing={2} mb={2}>
                            {[
                                { label: "User", key: "user" },
                                { label: "Table", key: "table" },
                                { label: "Queue", key: "queue" },
                                { label: "Action", key: "action" }
                            ].map(f => (
                                <Grid item key={f.key} xs={12} sm={6} md={3}>
                                    <TextField
                                        fullWidth
                                        label={f.label}
                                        value={(filters as any)[f.key]}
                                        onChange={(e) => setFilters({ ...filters, [f.key]: e.target.value })}
                                    />
                                </Grid>
                            ))}
                            <Grid item xs={12} sm={6} md={3}>
                                <TextField
                                    fullWidth
                                    type="date"
                                    label="From"
                                    InputLabelProps={{ shrink: true }}
                                    value={filters.from}
                                    onChange={(e) => setFilters({ ...filters, from: e.target.value })}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <TextField
                                    fullWidth
                                    type="date"
                                    label="To"
                                    InputLabelProps={{ shrink: true }}
                                    value={filters.to}
                                    onChange={(e) => setFilters({ ...filters, to: e.target.value })}
                                />
                            </Grid>
                            <Grid item xs={12} md={3} display="flex" alignItems="flex-end">
                                <Button variant="outlined" startIcon={<FileDownload />} onClick={exportHistory}>Export</Button>
                            </Grid>
                        </Grid>

                        <Box className="ag-theme-alpine" sx={{ height: 500 }}>
                            <AgGridReact rowData={filteredHistories} columnDefs={historyColumns} />
                        </Box>
                    </Paper>
                )}
            </Container>
        </Box>
    );
};

export default ApprovalAdmin;
